import { Button, CardContent, Container, Grid, Link, Typography } from '@material-ui/core'
import ContactSupportIcon from '@material-ui/icons/ContactSupport'
import { Establishment } from '@sugg-gestion/ubidreams-react-suggpro'
import clsx from 'clsx'
import FlatCard from 'components/common/flatCard'
import { WebsiteCustomizationFormState } from 'components/forms/establishment/websiteCustomizationForm'
import React from 'react'
import { Trans } from 'react-i18next'
import { useStyles } from './websiteCustomization.css'
import { EditModal } from './websiteCustomization/editModal'

interface Props {
    editModalOpen: boolean
    handleEditCustomization: () => void
    handleModalClose: () => void
    establishment: Establishment
    onSubmit: (values: WebsiteCustomizationFormState) => void
    inProgress: boolean
}

export const WebsiteCustomization: React.FC<Props> = ({
    establishment,
    editModalOpen,
    handleEditCustomization,
    handleModalClose,
    ...formData
}) => {
    const classes = useStyles()

    const getWebsiteCustomizationInitialValues = (): WebsiteCustomizationFormState => {
        let initialValues: WebsiteCustomizationFormState = {
            font: establishment.websiteFont ?? undefined,
            backgroundColorLight: establishment.websiteBackgroundColorLight ?? undefined,
            backgroundColorDark: establishment.websiteBackgroundColorDark ?? undefined,
            titleSectionTextColorEnabled: [],
            titleSectionBackgroundColorEnabled: [],
            titleSectionHidden: false,
            titleSectionSortOrder: 0,
            slateSectionTextColorEnabled: [],
            slateSectionBackgroundColorEnabled: [],
            slateSectionHidden: false,
            slateSectionSortOrder: 0,
            menuSectionTextColorEnabled: [],
            menuSectionBackgroundColorEnabled: [],
            menuSectionHidden: false,
            menuSectionSortOrder: 0,
            newsSectionTextColorEnabled: [],
            newsSectionBackgroundColorEnabled: [],
            newsSectionHidden: false,
            newsSectionSortOrder: 0,
            aboutSectionTextColorEnabled: [],
            aboutSectionBackgroundColorEnabled: [],
            aboutSectionHidden: false,
            aboutSectionSortOrder: 0,
            linksSectionTextColorEnabled: [],
            linksSectionBackgroundColorEnabled: [],
            linksSectionHidden: false,
            linksSectionSortOrder: 0,
            bookSectionTextColorEnabled: [],
            bookSectionBackgroundColorEnabled: [],
            bookSectionHidden: false,
            bookSectionSortOrder: 0,
            orderSectionTextColorEnabled: [],
            orderSectionBackgroundColorEnabled: [],
            orderSectionHidden: false,
            orderSectionSortOrder: 0,
            photosSectionTextColorEnabled: [],
            photosSectionBackgroundColorEnabled: [],
            photosSectionHidden: false,
            photosSectionSortOrder: 0,
            informationSectionTextColorEnabled: [],
            informationSectionBackgroundColorEnabled: [],
            informationSectionHidden: false,
            informationSectionSortOrder: 0,
            reviewsSectionTextColorEnabled: [],
            reviewsSectionBackgroundColorEnabled: [],
            reviewsSectionHidden: false,
            reviewsSectionSortOrder: 0,
            footerSectionTextColorEnabled: [],
            footerSectionBackgroundColorEnabled: [],
            footerSectionHidden: false,
            footerSectionSortOrder: 0,
            aboutSectionOverlayOpacityEnabled: [],
            bookSectionOverlayOpacityEnabled: [],
            footerSectionOverlayOpacityEnabled: [],
            informationSectionOverlayOpacityEnabled: [],
            linksSectionOverlayOpacityEnabled: [],
            menuSectionOverlayOpacityEnabled: [],
            newsSectionOverlayOpacityEnabled: [],
            orderSectionOverlayOpacityEnabled: [],
            photosSectionOverlayOpacityEnabled: [],
            reviewsSectionOverlayOpacityEnabled: [],
            slateSectionOverlayOpacityEnabled: [],
            titleSectionOverlayOpacityEnabled: [],
        }

        establishment.websiteSections?.forEach((section) => {
            // @ts-ignore
            initialValues[section.section + 'SectionTextColor'] = section.textColor
            if (section.textColor) {
                // @ts-ignore
                initialValues[section.section + 'SectionTextColorEnabled'] = [true]
            }
            // @ts-ignore
            initialValues[section.section + 'SectionBackgroundColor'] = section.backgroundColor
            if (section.backgroundColor) {
                // @ts-ignore
                initialValues[section.section + 'SectionBackgroundColorEnabled'] = [true]
            }
            if (
                section.section === 'title' &&
                (section.overlayOpacity === null || section.overlayOpacity === undefined)
            ) {
                initialValues.titleSectionOverlayOpacity = 0.5
            } else {
                // @ts-ignore
                initialValues[section.section + 'SectionOverlayOpacity'] = section.overlayOpacity
            }
            if (
                // @ts-ignore
                initialValues[section.section + 'SectionOverlayOpacity'] !== undefined &&
                // @ts-ignore
                initialValues[section.section + 'SectionOverlayOpacity'] !== null
            ) {
                // @ts-ignore
                initialValues[section.section + 'SectionOverlayOpacityEnabled'] = [true]
            }
            if (section.backgroundImage) {
                // @ts-ignore
                initialValues[section.section + 'SectionBackgroundImage'] = {
                    id: 0,
                    url: section.backgroundImage,
                }
            }
            if (section.sortOrder) {
                // @ts-ignore
                initialValues[section.section + 'SectionSortOrder'] = section.sortOrder
            }
            if (section.hidden) {
                // @ts-ignore
                initialValues[section.section + 'SectionHidden'] = section.hidden
            }
        })

        return initialValues
    }
    return (
        <FlatCard className={classes.root}>
            <CardContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Container disableGutters maxWidth="md" className={classes.customizationContainer}>
                            <Typography
                                variant="h1"
                                color="inherit"
                                className={clsx(classes.photosTitle, classes.title)}
                            >
                                <Trans i18nKey="views.externalLinks.customization.title" />
                                <div className={classes.actionsContainer}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={clsx(classes.action, classes.actionTitle)}
                                        onClick={handleEditCustomization}
                                    >
                                        <Trans i18nKey="actions.edit" />
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        className={clsx(classes.action, classes.title)}
                                        href="https://resto.suggpro.com/page-aide-site-web"
                                        target="_blank"
                                        endIcon={<ContactSupportIcon />}
                                    >
                                        <Trans i18nKey="actions.help" />
                                    </Button>
                                </div>
                            </Typography>
                        </Container>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>
                            <Trans
                                i18nKey="views.externalLinks.customization.description"
                                components={[
                                    <strong />,
                                    <strong className={classes.underline} />,
                                    <Link
                                        className={clsx(classes.underline, classes.title)}
                                        href="https://resto.suggpro.com/page-aide-site-web"
                                    />,
                                ]}
                            />
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
            <EditModal
                open={editModalOpen}
                onClose={handleModalClose}
                initialValues={getWebsiteCustomizationInitialValues()}
                {...formData}
            />
        </FlatCard>
    )
}
